import { UploadTask, RenderJob, TaskState, getTasks, getRenderJobs, useFetch, TASK_STATE_PENDING, TASK_STATE_RUNNING, TASK_STATE_COMPLETED, TASK_STATE_FAILED } from "../../shared/api";
import { ErrorDisplay, Loading, formatDateTime, usePageTitle } from "../../shared/utils";
import useSWR from "swr";
import { Link, useSearchParams } from "react-router-dom";
import { Box, Title, Group, Stack, Text, TextInput, NumberInput, Select, SegmentedControl, UnstyledButton } from "@mantine/core";
import { Pagination } from "../../shared/components/Pagination";
import { FilterBar, useFilterDraft } from "../../shared/components/FilterBar";
import { useEffect, useMemo } from "react";
import { StatusIcon } from "./StatusIcon";
import youtubeIcon from "../../shared/assets/colored/youtube.png";
import vkIcon from "../../shared/assets/colored/vk.png";
import './Tasks.css';

const platformIcons = { youtube: youtubeIcon, vk: vkIcon };
const STATE_OPTIONS = [{ value: TASK_STATE_PENDING, label: 'Ожидание' }, { value: TASK_STATE_RUNNING, label: 'В процессе' }, { value: TASK_STATE_COMPLETED, label: 'Успешно' }, { value: TASK_STATE_FAILED, label: 'Ошибка' }];
const RENDER_STATE_OPTIONS = [...STATE_OPTIONS, { value: 'cancelled', label: 'Отменено' }];

/** A render from the editor: no page of its own, so the row says it all. */
function RenderJobDisplay({ job }: { job: RenderJob }) {
  const name = job.output_path.split('/').pop() || job.output_path;
  const detail = job.state === TASK_STATE_RUNNING && job.percent != null ? ` · ${Math.round(job.percent)}%` : '';
  return (
    <Box className="taskListItem" style={{ cursor: 'default' }}>
      <Group gap="sm" wrap="nowrap" align="center">
        <StatusIcon state={job.state} size={20} />
        <Stack gap={0} style={{ flex: 1, minWidth: 0 }}>
          <Text fz="md" fw={500} truncate title={job.output_path}>{name}</Text>
          <Text fz="sm" c="dimmed">{job.created_by} · {formatDateTime(job.created_at)}{detail}</Text>
          {job.error && <Text fz="xs" c="red.6" lineClamp={2}>{job.error}</Text>}
        </Stack>
      </Group>
    </Box>
  );
}

function TaskDisplay({ task }: { task: UploadTask }) {
  const icon = platformIcons[task.target_platform as keyof typeof platformIcons];
  return (
    <UnstyledButton component={Link} to={`/tasks/${task.id}`} className="taskListItem">
      <Group gap="sm" wrap="nowrap" align="center">
        <StatusIcon state={task.state} size={20} />
        <Stack gap={0} style={{ flex: 1 }}>
          <Group gap="sm" wrap="nowrap"><Text fz="md" fw={500}>{task.target_title}</Text>{icon && <img src={icon} alt={task.target_platform} style={{ height: 'auto', width: 20, flexShrink: 0 }} />}</Group>
          <Text fz="sm" c="dimmed">{task.created_by}</Text>
        </Stack>
      </Group>
    </UnstyledButton>
  );
}

const MAX_PAGE_SIZE = 500;
const FILTERS_KEY = 'tasks:filters';

interface TaskFilters { mine: boolean; state: string; createdBy: string; limit: number }
const DEFAULT_FILTERS: TaskFilters = { mine: true, state: '', createdBy: '', limit: 10 };

// The last applied filters, so the page reopens on what the user looked at last
// (by default: only their own tasks).
function loadStoredFilters(): TaskFilters {
  try { return { ...DEFAULT_FILTERS, ...JSON.parse(localStorage.getItem(FILTERS_KEY) || '{}') }; } catch { return DEFAULT_FILTERS; }
}
function storeFilters(f: TaskFilters) {
  try { localStorage.setItem(FILTERS_KEY, JSON.stringify(f)); } catch { /* storage unavailable */ }
}

const toParams = (f: TaskFilters, page: number): Record<string, string> => {
  const p: Record<string, string> = { mine: f.mine ? '1' : '0', limit: String(f.limit), page: String(page) };
  if (f.state) p.state = f.state;
  if (!f.mine && f.createdBy.trim()) p.createdBy = f.createdBy.trim();
  return p;
};

export function Tasks() {
  usePageTitle("Задачи");
  const { fetch } = useFetch();
  const [searchParams, setSearchParams] = useSearchParams();

  // The URL holds the applied filters (shareable); without them, use the stored ones.
  useEffect(() => {
    if (!searchParams.has('mine')) {
      const p = toParams(loadStoredFilters(), 1);
      setSearchParams(searchParams.get('kind') === 'render' ? { ...p, kind: 'render' } : p, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  const renders = searchParams.get('kind') === 'render';
  const applied: TaskFilters = useMemo(() => ({
    mine: searchParams.get('mine') !== '0',
    state: searchParams.get('state') || '',
    createdBy: searchParams.get('createdBy') || '',
    limit: Math.min(MAX_PAGE_SIZE, Math.max(1, parseInt(searchParams.get('limit') ?? '10', 10) || 10)),
  }), [searchParams]);
  const page = Math.max(1, parseInt(searchParams.get('page') ?? '1', 10) || 1);
  const { draft, patch, dirty } = useFilterDraft(applied);

  const withKind = (p: Record<string, string>) => (renders ? { ...p, kind: 'render' } : p);
  const apply = (f: TaskFilters) => { storeFilters(f); setSearchParams(withKind(toParams(f, 1))); };
  // "Отменено" exists only for renders.
  const switchKind = (kind: string) => {
    const f = kind === 'render' || applied.state !== 'cancelled' ? applied : { ...applied, state: '' };
    setSearchParams(kind === 'render' ? { ...toParams(f, 1), kind } : toParams(f, 1));
  };

  const query = { limit: applied.limit, start: (page - 1) * applied.limit, mine: applied.mine, createdBy: applied.createdBy || undefined, fetch };
  const ready = searchParams.has('mine');
  const { data: tasks, error, isLoading } = useSWR(
    ready && !renders ? { ...query, state: (applied.state || undefined) as TaskState | undefined } : null,
    getTasks, { refreshInterval: 5_000 },
  );
  const { data: jobs, error: jobsError, isLoading: jobsLoading } = useSWR(
    ready && renders ? ['render_jobs', query.limit, query.start, applied.state, query.mine, query.createdBy] : null,
    () => getRenderJobs({ ...query, state: applied.state || undefined }), { refreshInterval: 5_000 },
  );
  const rows = renders ? jobs : tasks;

  if (error || jobsError) return <ErrorDisplay error={error || jobsError} />;

  return (
    <Box component="main" p="xl">
      <Box mb="xl">
        <Group justify="space-between" align="center" mb="lg">
          <Title order={2}>Задачи</Title>
          <SegmentedControl data={[{ value: 'upload', label: 'Публикации' }, { value: 'render', label: 'Рендеры' }]} value={renders ? 'render' : 'upload'} onChange={switchKind} />
        </Group>
        <Box mb="md">
          <FilterBar dirty={dirty} onApply={() => apply(draft)} onReset={() => apply(DEFAULT_FILTERS)}>
            <SegmentedControl data={[{ value: 'mine', label: 'Мои' }, { value: 'all', label: 'Все' }]} value={draft.mine ? 'mine' : 'all'} onChange={v => patch({ mine: v === 'mine' })} />
            <TextInput label="Создано:" placeholder="Фильтр по пользователю..." miw={200} disabled={draft.mine} value={draft.mine ? '' : draft.createdBy} onChange={e => patch({ createdBy: e.currentTarget.value })} />
            <Select label="Статус:" placeholder="Все" clearable w={160} data={renders ? RENDER_STATE_OPTIONS : STATE_OPTIONS} value={draft.state || null} onChange={v => patch({ state: v || '' })} />
            <NumberInput label="Размер страницы:" w={140} min={1} max={MAX_PAGE_SIZE} clampBehavior="strict" value={draft.limit} onChange={v => patch({ limit: Number(v) || 1 })} />
          </FilterBar>
        </Box>
        {rows && <Pagination page={page} shown={rows.length} hasNext={rows.length >= applied.limit} onChange={next => setSearchParams(withKind(toParams(applied, next)))} />}
      </Box>
      {renders
        ? (jobsLoading || !jobs ? <Loading /> : <Box>{jobs.map(j => <RenderJobDisplay key={j.id} job={j} />)}{jobs.length === 0 && <Text c="dimmed" ta="center" py="md">Рендеров нет</Text>}</Box>)
        : (isLoading || !tasks ? <Loading /> : <Box>{tasks.map(t => <TaskDisplay key={t.id} task={t} />)}</Box>)}
    </Box>
  );
}
